import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import dayjs from 'dayjs';
import EntertainmentCapacity from '../../components/EntertainmentCapacity';
import TimeLineBase from './TimelineBase';
import { applyOffsetToTime } from '../../../../util/timeSlots';

const GRID_START_OFFSET_PX = 27;

const getBookingCardId = element => {
  if (!element || typeof element.getAttribute !== 'function') return null;
  const bookingId = element.getAttribute('data-booking-id');
  if (bookingId) return bookingId;
  const testId = element.getAttribute('data-testid') || '';
  return testId.startsWith('booking-card-') ? testId.slice('booking-card-'.length) : null;
};

const getVisibleArea = (rect, bounds) => {
  const visibleWidth = Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left);
  const visibleHeight = Math.min(rect.bottom, bounds.bottom) - Math.max(rect.top, bounds.top);
  if (visibleWidth <= 0 || visibleHeight <= 0) return 0;
  return visibleWidth * visibleHeight;
};

const areStringArraysEqual = (a, b) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

// Module-level: no component dependencies, never changes
const isInHeaderOrSidebar = target => {
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(
    target.closest('.MuiAppBar-root, .MuiToolbar-root, .MuiDrawer-root, .MuiDrawer-paper') ||
    target.closest('[data-testid="sidebar"], .sidebar, .MuiDrawer-root, .MuiDrawer-paper')
  );
};

const gentleWave = keyframes`
  0%, 100% { 
    opacity: 0.35;
    transform: scaleX(1) scaleY(1);
  }
  25% { 
    opacity: 0.5;
    transform: scaleX(1.02) scaleY(0.98);
  }
  50% { 
    opacity: 0.45;
    transform: scaleX(0.98) scaleY(1.02);
  }
  75% { 
    opacity: 0.55;
    transform: scaleX(1.01) scaleY(0.99);
  }
`;

// Animated diagonal stripes that move to convey loading state more clearly
const barberPole = keyframes`
  0% {
    background-position: 0 0, 0 0;
  }
  100% {
    background-position: 40px 0, 0 0;
  }
`;

const LoadingOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  height: '100%',
  zIndex: 1,
  pointerEvents: 'none',
  background: [
    theme.palette.mode === 'light'
      ? 'repeating-linear-gradient(45deg, rgba(255,255,255,0.5) 0px, rgba(255,255,255,0.5) 10px, rgba(255,255,255,0.2) 10px, rgba(255,255,255,0.2) 20px)'
      : 'repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.15) 10px, rgba(255,255,255,0.05) 10px, rgba(255,255,255,0.05) 20px)',
    'linear-gradient(135deg, rgba(25, 95, 190, 0.55) 0%, rgba(35, 120, 210, 0.6) 30%, rgba(25, 100, 185, 0.55) 50%, rgba(45, 130, 225, 0.6) 70%, rgba(25, 95, 190, 0.55) 100%)',
  ].join(', '),
  backgroundSize: '40px 100%, cover',
  backgroundBlendMode: 'overlay, normal',
  transformOrigin: 'center',
  animation: `${gentleWave} 4.2s ease-in-out infinite, ${barberPole} 1.2s linear infinite`,
  WebkitMaskImage:
    'linear-gradient(to right, rgba(0,0,0,0) 0px, rgba(0,0,0,1) 40px, rgba(0,0,0,1) calc(100% - 40px), rgba(0,0,0,0) 100%)',
  maskImage:
    'linear-gradient(to right, rgba(0,0,0,0) 0px, rgba(0,0,0,1) 40px, rgba(0,0,0,1) calc(100% - 40px), rgba(0,0,0,0) 100%)',
  boxShadow:
    theme.palette.mode === 'light'
      ? 'inset 40px 0 24px -24px rgba(0,0,0,0.12), inset -40px 0 24px -24px rgba(0,0,0,0.12)'
      : 'inset 40px 0 24px -24px rgba(0,0,0,0.35), inset -40px 0 24px -24px rgba(0,0,0,0.35)',
}));

const RedLine = styled('div')({
  position: 'absolute',
  top: 0,
  left: '10px',
  height: '10px',
  width: '2px',
  backgroundColor: '#ff1744',
  zIndex: 1000,
  pointerEvents: 'none',
  boxShadow: '0 0 6px rgba(255, 23, 68, 0.95)',
});

const GridLine = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: '10px',
  height: '10px',
  width: '1px',
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[400] : '#393939',
  zIndex: 1,
}));

/**
 * Bar-chart row rendered above the booking grid.
 *
 * When `syncWithGrid` is true (Golf / interval-mode entertainments) the row is
 * scroll-synchronised with the booking grid and each bar has a fixed pixel width
 * that matches the grid's time slots so the bars align precisely.
 *
 * When `syncWithGrid` is false the row is rendered as a self-contained,
 * independently-scrollable component that fits the visible screen width.
 */
const CapacityBarRow = ({ data, slotWidthPx, startOffsetPx, gridContainerRef, onToggleExpand, syncWithGrid = false }) => {
  const barRowRef = useRef(null);
  const isSyncingFromBar = useRef(false);
  const isSyncingFromGrid = useRef(false);

  useEffect(() => {
    if (!syncWithGrid) return;
    const barEl = barRowRef.current;
    const gridEl = gridContainerRef?.current;
    if (!barEl || !gridEl) return;

    const handleBarScroll = () => {
      if (isSyncingFromGrid.current) return;
      isSyncingFromBar.current = true;
      gridEl.scrollLeft = barEl.scrollLeft;
      isSyncingFromBar.current = false;
    };

    const handleGridScroll = () => {
      if (isSyncingFromBar.current) return;
      isSyncingFromGrid.current = true;
      barEl.scrollLeft = gridEl.scrollLeft;
      isSyncingFromGrid.current = false;
    };

    barEl.addEventListener('scroll', handleBarScroll, { passive: true });
    gridEl.addEventListener('scroll', handleGridScroll, { passive: true });

    // Sync initial scroll position from the grid to the bar row
    barEl.scrollLeft = gridEl.scrollLeft;

    return () => {
      barEl.removeEventListener('scroll', handleBarScroll);
      gridEl.removeEventListener('scroll', handleGridScroll);
    };
  }, [gridContainerRef, syncWithGrid]);

  // Derive effective bar width based on the capacity data's time interval vs grid's 30-min slot width.
  // slotWidthPx is px per 30-minute slot in the grid.
  // If the capacity data uses 15-minute intervals, each bar should be slotWidthPx/2 wide.
  const effectiveBarWidthPx = (() => {
    const times = data?.times;
    if (!Array.isArray(times) || times.length < 2) return slotWidthPx;
    const parseMinutes = t => {
      const [h, m] = String(t?.time || '').split(':').map(Number);
      return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
    };
    const m0 = parseMinutes(times[0]);
    const m1 = parseMinutes(times[1]);
    if (m0 == null || m1 == null) return slotWidthPx;
    const intervalMinutes = m1 - m0;
    if (intervalMinutes <= 0) return slotWidthPx;
    return Math.round((slotWidthPx * intervalMinutes) / 30);
  })();

  // In non-sync mode, render a self-contained standalone bar chart (same as Resources view).
  if (!syncWithGrid) {
    return (
      <Box sx={{ mb: '4px' }}>
        <EntertainmentCapacity
          data={data}
          condensedMode={false}
          onToggleExpand={onToggleExpand}
        />
      </Box>
    );
  }

  const totalWidth = startOffsetPx + effectiveBarWidthPx * (data?.times?.length || 0);

  return (
    <Box
      ref={barRowRef}
      sx={{
        overflowX: 'auto',
        overflowY: 'hidden',
        mb: '4px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {/* Inner container defines the scrollable width */}
      <Box sx={{ width: totalWidth, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        {(data.entertainmentName || data.capacity != null) && (
          <Box
            sx={{
              position: 'sticky',
              left: 0,
              zIndex: 5,
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: 0.75,
              px: 1,
              py: 0.25,
              background: theme => theme.palette.background.paper,
              whiteSpace: 'nowrap',
              borderRadius: '0 4px 4px 0',
              alignSelf: 'flex-start',
            }}
          >
            {data.entertainmentName && (
              <Typography variant='subtitle2' sx={{ fontWeight: 700, lineHeight: 1 }}>
                {data.entertainmentName}
              </Typography>
            )}
            {data.capacity != null && (
              <Typography variant='caption' sx={{ opacity: 0.6, lineHeight: 1 }}>
                Slotkapazität: {data.capacity}
              </Typography>
            )}
            {data.totalBookings != null && (
              <Typography variant='caption' sx={{ opacity: 0.6, lineHeight: 1 }}>
                · {data.totalBookings} Buchung{data.totalBookings !== 1 ? 'en' : ''}
              </Typography>
            )}
            {data.ageSplit && (
              <Typography variant='caption' sx={{ opacity: 0.55, lineHeight: 1 }}>
                · Kinder-Kapazität wird schraffiert dargestellt
              </Typography>
            )}
          </Box>
        )}
        {/* Bar chart aligned with the grid: left offset matches GRID_START_OFFSET_PX */}
        <Box sx={{ pl: `${startOffsetPx}px`, display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
          <EntertainmentCapacity
            data={data}
            condensedMode={false}
            onToggleExpand={onToggleExpand}
            slotWidthPx={effectiveBarWidthPx}
            startOffsetPx={0}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default function BookingGridBase({
  bookings = [],
  rowCount = 0,
  date = dayjs(),
  slotCapacity = 0,
  calcAttendance,
  renderAvailableSlots = true,
  slotWidthPx = 140,
  minutesPerSlot = 30,
  renderTimeline = ({ times, showTimeRow, showSlotRows, hideTimeRow }) => (
    <TimeLineBase
      times={times}
      renderAvailableSlots={renderAvailableSlots}
      slotWidth={slotWidthPx}
      showTimeRow={showTimeRow}
      showSlotRows={showSlotRows}
      hideTimeRow={hideTimeRow}
    />
  ),
  renderBookingCard,
  renderLegend,
  renderHeaderExtras,
  capacityData,
  rowHeight = 45,
  headerHeight = 50,
  gridLineTop = 22,
  heightCalculator = rc =>
    `calc(${headerHeight}px + ${rowHeight} * ${Math.max(1, rc)}px - ${
      renderAvailableSlots ? 0 : gridLineTop
    }px)`,
  availableShiftTimes,
  draggedBookingDuration,
  isLoadingShiftTimes,
  shiftTimeOffsetMinutes = 0,
  laneBackgrounds = null,
  renderLaneLabel,
  laneLabelWidth = 26,
  rowTopOffset = renderAvailableSlots ? 40 : 16,
  nearestCount = 4,
  standaloneCapacityBar = false,
}) {
  const theme = useTheme();
  const [redLinePosition, setRedLinePosition] = useState(10);
  const [times, setTimes] = useState([]);
  const [redLineVisible, setRedLineVisible] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef();
  const timelineContainerRef = useRef();
  const stickyRootRef = useRef(null);
  const stickyCapacityRef = useRef(null);
  const stickyHeaderExtrasRef = useRef(null);
  const [centerTime, setCenterTime] = useState(null);
  const [capacityExpanded, setCapacityExpanded] = useState(false);
  const [visibleBookingIds, setVisibleBookingIds] = useState([]);

  const defaultCalcAttendance = () => {
    return Array.from({ length: (24 - 9) * 2 + 1 }, (_, index) => {
      const hour = 9 + Math.floor(index / 2);
      const minute = index % 2 === 0 ? '00' : '30';
      const formattedTime = `${String(hour).padStart(2, '0')}:${minute}`;
      return {
        isFull: index % 2 === 0,
        time: formattedTime,
        qualifiedTime: new Date(
          date.get('year'),
          date.get('month'),
          date.get('date'),
          hour,
          minute
        ),
        i: index,
        occupied: 0,
        free: slotCapacity,
      };
    });
  };

  const calcRedLineLeft = useCallback(() => {
    const now = new Date();
    const hourWidth = slotWidthPx * (60 / minutesPerSlot);
    return (
      (now.getHours() - 9) * hourWidth + (now.getMinutes() / 60) * hourWidth + GRID_START_OFFSET_PX
    );
  }, [slotWidthPx, minutesPerSlot]);

  // Helper function to check if an event target is within header or sidebar areas - defined at module level above

  useEffect(() => {
    const newTimes = calcAttendance ? calcAttendance(bookings) : defaultCalcAttendance(bookings);
    setTimes(newTimes);
  }, [bookings, slotCapacity, calcAttendance]);

  useEffect(() => {
    if (date.isSame(dayjs(), 'day')) {
      const newPos = calcRedLineLeft();
      setRedLinePosition(newPos);
      setRedLineVisible(true);
      const scrollTo = newPos - 100;
      if (scrollTo > 0 && containerRef.current) containerRef.current.scrollLeft = scrollTo;
    } else {
      setRedLineVisible(false);
    }
  }, [date, calcRedLineLeft]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newPos = calcRedLineLeft();
      setRedLinePosition(newPos);
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [calcRedLineLeft]);

  // horizontal scroll (mouse & touch)
  useEffect(() => {
    const computeCenterTime = () => {
      if (!containerRef.current) return;
      const centerX = containerRef.current.scrollLeft + containerRef.current.clientWidth / 2;
      const slotsFromStart = (centerX - 27) / slotWidthPx; // 0 at 09:00
      const minutesFromStart = slotsFromStart * minutesPerSlot;
      const base = new Date(date.get('year'), date.get('month'), date.get('date'), 9, 0, 0, 0);
      const dt = new Date(base.getTime() + Math.round(minutesFromStart) * 60 * 1000);
      setCenterTime(dt);
    };

    // centerTime only feeds the optional "nearest bookings" header (Golf/Bash) and
    // does not need per-frame precision. Updating it on every scroll frame
    // re-renders the whole grid (and every booking card), which is the main cause
    // of horizontal-scroll jank that worsens with booking count. Update it only
    // after scrolling has settled.
    let centerTimeRafId = null;
    let centerTimeDebounceId = null;
    const scheduleCenterTimeUpdate = () => {
      if (centerTimeDebounceId) clearTimeout(centerTimeDebounceId);
      centerTimeDebounceId = setTimeout(() => {
        centerTimeDebounceId = null;
        centerTimeRafId = requestAnimationFrame(() => {
          centerTimeRafId = null;
          computeCenterTime();
        });
      }, 120);
    };

    // Helper: Detect if any MUI modal/dialog is currently open
    const isModalOpen = () => {
      // MUI renders an element with the class `MuiModal-root` (used by Modal & Dialog)
      return Boolean(document.querySelector('.MuiModal-root'));
    };

    let startX, startY, initialScrollLeft, initialScrollTop;
    let isDraggingLocal = false;

    // Global wheel event listener for scroll events outside the grid
    const handleGlobalWheel = event => {
      // If a modal is open, don't intercept – allow default scrolling in modal
      if (isModalOpen()) return;

      // Check if the event occurs within header or sidebar areas - exclude these
      const target = event.target;
      if (isInHeaderOrSidebar(target)) {
        return;
      }

      // Check if the event occurs outside the grid container
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        // Event occurs outside - ALWAYS scroll horizontally (like with Shift)
        if (containerRef.current.scrollWidth > containerRef.current.clientWidth) {
          // Grid is horizontally scrollable
          const currentScrollLeft = containerRef.current.scrollLeft;
          const maxScrollLeft = containerRef.current.scrollWidth - containerRef.current.clientWidth;

          // Calculate new scroll position (always horizontal, regardless of Shift)
          let newScrollLeft = currentScrollLeft - event.deltaY;

          // Limit scroll to valid range
          newScrollLeft = Math.max(0, Math.min(newScrollLeft, maxScrollLeft));

          // Only scroll if position actually changes
          if (newScrollLeft !== currentScrollLeft) {
            containerRef.current.scrollLeft = newScrollLeft;
          }
        }

        // always prevent event when it occurs outside the grid
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
    };

    const handleWheel = event => {
      // Skip interception when any modal/dialog is open
      if (isModalOpen()) return;

      // Check if the event occurs within header or sidebar areas - exclude these
      const target = event.target;
      if (isInHeaderOrSidebar(target)) {
        return;
      }

      if (containerRef.current) {
        // Standard: horizontaler Scroll lokal
        containerRef.current.scrollLeft -= event.deltaY + event.deltaX;
        event.preventDefault();
        scheduleCenterTimeUpdate();
      }
    };

    // Caches for the current drag session (mouse only)
    let verticalParent = null;
    let latestClientX, latestClientY;
    let animationFrameId = null;

    const scrollUpdate = () => {
      if (!containerRef.current || !isDraggingLocal) {
        animationFrameId = null;
        return;
      }

      const deltaX = latestClientX - startX;
      const deltaY = latestClientY - startY;

      containerRef.current.scrollLeft = initialScrollLeft - deltaX;

      if (verticalParent) {
        verticalParent.scrollTop = initialScrollTop - deltaY;
      } else {
        window.scrollTo(window.scrollX, initialScrollTop - deltaY);
      }

      animationFrameId = requestAnimationFrame(scrollUpdate);
      scheduleCenterTimeUpdate();
    };

    const handleMouseDown = event => {
      if (event.button === 0) {
        // If the click is on a booking card, don't start grid dragging.
        // The card has its own drag handler.
        if (event.target.closest('.MuiCard-root')) {
          return;
        }
        // Check if the event occurs within header or sidebar areas - exclude these
        const target = event.target;
        if (isInHeaderOrSidebar(target)) {
          return;
        }

        // left mouse button only
        isDraggingLocal = true;
        setIsDragging(true);
        startX = event.clientX;
        startY = event.clientY;
        latestClientX = event.clientX;
        latestClientY = event.clientY;
        if (containerRef.current) {
          initialScrollLeft = containerRef.current.scrollLeft;

          // Cache the first scrollable vertical parent for this drag session
          verticalParent = null;
          let parent = containerRef.current.parentElement;
          while (parent && parent !== document.body) {
            const { overflowY } = window.getComputedStyle(parent);
            if (
              (overflowY === 'auto' || overflowY === 'scroll') &&
              parent.scrollHeight > parent.clientHeight
            ) {
              verticalParent = parent;
              initialScrollTop = parent.scrollTop;
              break;
            }
            parent = parent.parentElement;
          }
          if (!verticalParent) {
            initialScrollTop = window.scrollY;
          }
        }
        event.preventDefault();
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = requestAnimationFrame(scrollUpdate);
      }
    };

    const handleMouseMove = event => {
      if (isDraggingLocal) {
        latestClientX = event.clientX;
        latestClientY = event.clientY;
        event.preventDefault();
      }
    };

    const handleMouseUp = () => {
      isDraggingLocal = false;
      setIsDragging(false);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    };

    // Track if the current touch gesture is intended for horizontal scrolling.
    let isHorizontalGesture = null;

    const handleTouchStart = event => {
      // Ignore events in header / sidebar
      const target = event.target;
      if (isInHeaderOrSidebar(target)) {
        return;
      }

      // If the touch is on a booking card, don't interfere with its drag handling
      if (event.target.closest('.MuiCard-root')) {
        return;
      }

      const touch = event.touches[0];
      startX = touch.pageX;
      startY = touch.pageY;
      isHorizontalGesture = null; // reset for new gesture

      if (containerRef.current) {
        initialScrollLeft = containerRef.current.scrollLeft;
      }
    };

    const handleTouchMove = event => {
      const target = event.target;
      if (isInHeaderOrSidebar(target)) {
        return;
      }

      // If the touch is on a booking card, don't interfere
      if (event.target.closest('.MuiCard-root')) {
        return;
      }

      const touch = event.touches[0];
      const deltaX = touch.pageX - startX;
      const deltaY = touch.pageY - startY;

      // Decide gesture intention on first move
      if (isHorizontalGesture === null) {
        isHorizontalGesture = Math.abs(deltaX) > Math.abs(deltaY);
      }

      if (!containerRef.current) return;

      if (isHorizontalGesture) {
        // For horizontal gestures rely on native scrolling (better inertia)
        scheduleCenterTimeUpdate();
        return; // let browser handle horizontal scroll
      }
      // For vertical gestures we do nothing, allow page scrolling
    };

    const elem = containerRef.current;
    if (elem) {
      elem.addEventListener('wheel', handleWheel);
      elem.addEventListener('mousedown', handleMouseDown);
      elem.addEventListener('touchstart', handleTouchStart);
      elem.addEventListener('touchmove', handleTouchMove);
      elem.addEventListener('scroll', scheduleCenterTimeUpdate, { passive: true });
      scheduleCenterTimeUpdate();
    }

    // Add global mouse events for dragging
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);
    // A single capture-phase listener on document already sees every wheel event in
    // the page; a second one on window only doubles the per-event work.
    document.addEventListener('wheel', handleGlobalWheel, { capture: true, passive: false });

    // Add global touch events for dragging
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      if (elem) {
        elem.removeEventListener('wheel', handleWheel);
        elem.removeEventListener('mousedown', handleMouseDown);
        elem.removeEventListener('touchstart', handleTouchStart);
        elem.removeEventListener('touchmove', handleTouchMove);
        elem.removeEventListener('scroll', scheduleCenterTimeUpdate);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
      document.removeEventListener('wheel', handleGlobalWheel, { capture: true, passive: false });
      document.removeEventListener('touchmove', handleTouchMove, { passive: true }); // Remove global touch listener
      document.removeEventListener('touchend', handleMouseUp); // Remove global touch end listener
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      if (centerTimeRafId) {
        cancelAnimationFrame(centerTimeRafId);
      }
      if (centerTimeDebounceId) {
        clearTimeout(centerTimeDebounceId);
      }
    };
  }, [date, slotWidthPx]);

  const relevantBookings = useMemo(
    () => (Array.isArray(bookings) ? bookings.filter(booking => booking && booking.status !== 'blocked') : []),
    [bookings]
  );

  const bookingById = useMemo(() => {
    const map = new Map();
    relevantBookings.forEach(booking => {
      map.set(String(booking._id), booking);
    });
    return map;
  }, [relevantBookings]);

  useEffect(() => {
    if (!renderHeaderExtras) {
      setVisibleBookingIds([]);
      return undefined;
    }

    let frameId = null;

    const updateVisibleBookings = () => {
      frameId = null;
      const containerEl = containerRef.current;
      if (!containerEl) return;

      const containerRect = containerEl.getBoundingClientRect();
      const stickyBottom = stickyRootRef.current?.getBoundingClientRect?.().bottom || 0;
      const bounds = {
        left: Math.max(0, containerRect.left),
        right: Math.min(window.innerWidth, containerRect.right),
        top: Math.max(0, containerRect.top, stickyBottom),
        bottom: Math.min(window.innerHeight, containerRect.bottom),
      };

      if (bounds.right <= bounds.left || bounds.bottom <= bounds.top) {
        setVisibleBookingIds(prev => (prev.length === 0 ? prev : []));
        return;
      }

      const viewportCenterX = bounds.left + (bounds.right - bounds.left) / 2;
      const viewportCenterY = bounds.top + (bounds.bottom - bounds.top) / 2;
      const visibleEntriesById = new Map();
      const cardElements = containerEl.querySelectorAll('[data-booking-id], [data-testid^="booking-card-"]');

      cardElements.forEach(cardEl => {
        const bookingId = getBookingCardId(cardEl);
        if (!bookingId || !bookingById.has(bookingId)) return;

        const rect = cardEl.getBoundingClientRect();
        const visibleArea = getVisibleArea(rect, bounds);
        if (visibleArea <= 0) return;

        const centerDistance =
          Math.abs((rect.left + rect.right) / 2 - viewportCenterX) +
          Math.abs((rect.top + rect.bottom) / 2 - viewportCenterY);

        const existing = visibleEntriesById.get(bookingId);
        if (!existing) {
          visibleEntriesById.set(bookingId, {
            bookingId,
            visibleArea,
            centerDistance,
            top: rect.top,
            left: rect.left,
          });
          return;
        }

        visibleEntriesById.set(bookingId, {
          bookingId,
          visibleArea: Math.max(existing.visibleArea, visibleArea),
          centerDistance: Math.min(existing.centerDistance, centerDistance),
          top: Math.min(existing.top, rect.top),
          left: Math.min(existing.left, rect.left),
        });
      });

      const nextVisibleIds = Array.from(visibleEntriesById.values())
        .sort((a, b) => {
          if (b.visibleArea !== a.visibleArea) return b.visibleArea - a.visibleArea;
          if (a.centerDistance !== b.centerDistance) return a.centerDistance - b.centerDistance;
          if (a.top !== b.top) return a.top - b.top;
          return a.left - b.left;
        })
        .slice(0, nearestCount)
        .map(entry => entry.bookingId);

      setVisibleBookingIds(prev => (areStringArraysEqual(prev, nextVisibleIds) ? prev : nextVisibleIds));
    };

    // This reads every card's layout box (querySelectorAll + getBoundingClientRect),
    // which forces a reflow. Running it per scroll frame is an O(bookings) cost that
    // scales badly. Debounce so it only runs once scrolling has settled; the header
    // list it feeds does not need to update mid-flick.
    let debounceId = null;
    const scheduleVisibleBookingsUpdate = () => {
      if (debounceId != null) clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        debounceId = null;
        if (frameId != null) return;
        frameId = requestAnimationFrame(updateVisibleBookings);
      }, 120);
    };

    const containerEl = containerRef.current;
    updateVisibleBookings();

    containerEl?.addEventListener('scroll', scheduleVisibleBookingsUpdate, { passive: true });
    window.addEventListener('resize', scheduleVisibleBookingsUpdate);
    window.addEventListener('scroll', scheduleVisibleBookingsUpdate, true);

    return () => {
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
      if (debounceId != null) {
        clearTimeout(debounceId);
      }
      containerEl?.removeEventListener('scroll', scheduleVisibleBookingsUpdate);
      window.removeEventListener('resize', scheduleVisibleBookingsUpdate);
      window.removeEventListener('scroll', scheduleVisibleBookingsUpdate, true);
    };
  }, [bookingById, nearestCount, renderHeaderExtras]);

  const nearestBookings = useMemo(() => {
    try {
      if (visibleBookingIds.length > 0) {
        return visibleBookingIds.map(id => bookingById.get(id)).filter(Boolean);
      }

      if (relevantBookings.length === 0) return [];
      const center = centerTime ? centerTime.getTime() : new Date().getTime();
      const closest = relevantBookings
        .slice()
        .sort(
          (a, b) =>
            Math.abs(new Date(a.startTime).getTime() - center) -
            Math.abs(new Date(b.startTime).getTime() - center)
        )
        .slice(0, nearestCount);
      closest.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
      return closest;
    } catch (e) {
      return relevantBookings.slice(0, nearestCount);
    }
  }, [bookingById, centerTime, nearestCount, relevantBookings, visibleBookingIds]);

  const capacityOverviewData = useMemo(() => {
    if (!capacityData) return null;

    const sumMatchingKeys = (obj, re) => {
      try {
        return Object.keys(obj || {}).reduce((sum, key) => {
          if (!re.test(key)) return sum;
          const v = Number(obj[key]);
          return sum + (Number.isFinite(v) ? v : 0);
        }, 0);
      } catch {
        return 0;
      }
    };

    if (Array.isArray(capacityData.times) && capacityData.times.length > 0) {
      const cap = Number(capacityData?.capacity);
      const localByTimeForApi = new Map();
      if (Array.isArray(times)) {
        for (const t of times) {
          if (t.time != null) localByTimeForApi.set(String(t.time), t);
        }
      }
      const sgt = capacityData.slotGroupTypes;

      const overviewTimes = capacityData.times.map(t => {
        const apiOcc = Number(t?.occupied ?? 0) || 0;
        const free = Number(t?.free ?? 0) || 0;
        const local = localByTimeForApi.get(String(t?.time));

        const occ = local
          ? sumMatchingKeys(local, /^s\d+$/) || Number(local?.capacityOccupied ?? 0) || 0
          : apiOcc;

        const pct = Number.isFinite(cap) && cap > 0
          ? Math.round((occ / cap) * 100)
          : occ + free > 0 ? Math.round((occ / (occ + free)) * 100) : 0;
        const base = { time: t?.time, occupied: occ, free: t?.free != null ? Number(t.free) : null, percentageOccupied: pct, absoluteCount: t?.absoluteCount };
        if (local && local.freesKids != null) {
          base.kidsCount = local.kidsCount || 0;
          base.adultsCount = local.adultsCount || 0;
          base.freesKids = local.freesKids;
          base.freesAdults = local.freesAdults;
          if (local.totalKidsCapacity != null) base.totalKidsCapacity = local.totalKidsCapacity;
          if (local.totalAdultsCapacity != null) base.totalAdultsCapacity = local.totalAdultsCapacity;
        }
        if (sgt && t?.time != null && sgt[String(t.time)] !== undefined) {
          base.slotGroupType = sgt[String(t.time)];
        }
        return base;
      });

      const hasLocalAgeSplitForApi = Array.isArray(times) && times.some(t => t.freesKids != null);

      return {
        entertainmentName: capacityData?.entertainmentName,
        usesResources: capacityData?.usesResources ?? true,
        capacity: capacityData?.capacity,
        totalBookings: capacityData?.totalBookings,
        times: overviewTimes,
        ...(hasLocalAgeSplitForApi ? { ageSplit: true } : {}),
        ...(sgt ? { slotGroupTypes: sgt } : {}),
      };
    }

    // Fall back to locally computed times only when the API did not provide
    // a precomputed utilization series.
    if (Array.isArray(times) && times.length > 0) {
      const cap = Number(capacityData?.capacity);
      const hasAgeSplit = times.some(t => t.freesKids != null);
      const sgtLocal = capacityData?.slotGroupTypes;
      const overviewTimes = times.map(t => {
        const occFromTeams = sumMatchingKeys(t, /^s\d+$/);
        const freeFromTeams = sumMatchingKeys(t, /^frees\d+$/);
        const fallbackOcc = Number(t?.capacityOccupied ?? t?.occupied ?? 0) || 0;
        const fallbackFree = Number(t?.capacityFree ?? t?.free ?? 0) || 0;
        const hasTeamKeys = occFromTeams > 0 || freeFromTeams !== 0;
        const occupied = hasTeamKeys ? occFromTeams : fallbackOcc;
        const free = hasTeamKeys ? freeFromTeams : fallbackFree;

        const pct =
          Number.isFinite(cap) && cap > 0
            ? Math.round((occupied / cap) * 100)
            : Number.isFinite(occupied + free) && occupied + free > 0
              ? Math.round((occupied / (occupied + free)) * 100)
              : 0;

        return {
          time: t?.time,
          occupied,
          free,
          percentageOccupied: pct,
          ...(hasAgeSplit && t.freesKids != null ? {
            kidsCount: t.kidsCount || 0,
            adultsCount: t.adultsCount || 0,
            freesKids: t.freesKids,
            freesAdults: t.freesAdults,
            ...(t.totalKidsCapacity != null ? { totalKidsCapacity: t.totalKidsCapacity } : {}),
            ...(t.totalAdultsCapacity != null ? { totalAdultsCapacity: t.totalAdultsCapacity } : {}),
          } : {}),
          ...(sgtLocal && t?.time != null && sgtLocal[String(t.time)] !== undefined
            ? { slotGroupType: sgtLocal[String(t.time)] }
            : t?.slotGroupType !== undefined ? { slotGroupType: t.slotGroupType } : {}),
        };
      });

      return {
        entertainmentName: capacityData?.entertainmentName,
        usesResources: capacityData?.usesResources ?? true,
        capacity: capacityData?.capacity,
        totalBookings: capacityData?.totalBookings,
        times: overviewTimes,
        ...(hasAgeSplit ? { ageSplit: true } : {}),
        ...(sgtLocal ? { slotGroupTypes: sgtLocal } : {}),
      };
    }

    const fallback = capacityData || null;

    // Enrich any result with age-split data from local times when available
    const hasLocalAgeSplit = Array.isArray(times) && times.some(t => t.freesKids != null);
    const resultToEnrich = fallback;
    if (hasLocalAgeSplit && resultToEnrich && Array.isArray(resultToEnrich.times) && resultToEnrich.times.length > 0) {
      const localByTime = new Map();
      for (const t of times) {
        if (t.time != null && t.freesKids != null) localByTime.set(String(t.time), t);
      }
      return {
        ...resultToEnrich,
        ageSplit: true,
        times: resultToEnrich.times.map(t => {
          const local = localByTime.get(String(t.time));
          if (!local) return t;
          return {
            ...t,
            kidsCount: local.kidsCount || 0,
            adultsCount: local.adultsCount || 0,
            freesKids: local.freesKids,
            freesAdults: local.freesAdults,
            ...(local.totalKidsCapacity != null ? { totalKidsCapacity: local.totalKidsCapacity } : {}),
            ...(local.totalAdultsCapacity != null ? { totalAdultsCapacity: local.totalAdultsCapacity } : {}),
          };
        }),
      };
    }

    return fallback;
  }, [times, capacityData]);

  useEffect(() => {
    const timelineEl = timelineContainerRef.current;
    const gridEl = containerRef.current;
    if (!timelineEl || !gridEl) {
      return;
    }

    let isSyncingFromTimeline = false;
    let isSyncingFromGrid = false;

    const handleTimelineScroll = () => {
      if (isSyncingFromGrid) {
        return;
      }

      isSyncingFromTimeline = true;
      gridEl.scrollLeft = timelineEl.scrollLeft;
      isSyncingFromTimeline = false;
    };

    const handleGridScroll = () => {
      if (isSyncingFromTimeline) {
        return;
      }

      isSyncingFromGrid = true;
      timelineEl.scrollLeft = gridEl.scrollLeft;
      isSyncingFromGrid = false;
    };

    timelineEl.addEventListener('scroll', handleTimelineScroll, { passive: true });
    gridEl.addEventListener('scroll', handleGridScroll, { passive: true });
    timelineEl.scrollLeft = gridEl.scrollLeft;

    return () => {
      timelineEl.removeEventListener('scroll', handleTimelineScroll);
      gridEl.removeEventListener('scroll', handleGridScroll);
    };
  }, [times, slotWidthPx, capacityExpanded]);

  return (
    <>
      <Box
        ref={stickyRootRef}
        sx={{
          position: 'sticky',
          top: 'var(--booking-filter-bar-bottom, 120px)',
          zIndex: 320,
          backgroundColor: 'background.default',
          margin: 0,
          padding: 0,
        }}
      >
        {renderLegend && renderLegend()}

        {capacityOverviewData && !capacityExpanded && (
          <div ref={stickyCapacityRef}>
            <EntertainmentCapacity
              data={capacityOverviewData}
              condensedMode={true}
              onToggleExpand={() => setCapacityExpanded(v => !v)}
              currentDate={date}
              autoScrollToCurrentTime={date.isSame(dayjs(), 'day')}
              highlightCurrentSlot={date.isSame(dayjs(), 'day')}
            />
          </div>
        )}

        {capacityOverviewData && capacityExpanded && (
          <div ref={stickyCapacityRef}>
            <CapacityBarRow
              data={capacityOverviewData}
              slotWidthPx={slotWidthPx}
              startOffsetPx={GRID_START_OFFSET_PX}
              gridContainerRef={containerRef}
              onToggleExpand={() => setCapacityExpanded(v => !v)}
              syncWithGrid={standaloneCapacityBar ? false : (() => {
                const times = capacityOverviewData?.times;
                if (!Array.isArray(times) || times.length < 2) return false;
                const parse = t => {
                  const [h, m] = String(t?.time || '').split(':').map(Number);
                  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
                };
                const m0 = parse(times[0]);
                const m1 = parse(times[1]);
                if (m0 == null || m1 == null) return false;
                return (m1 - m0) < 30;
              })()}
            />
          </div>
        )}

        {renderHeaderExtras && (
          <div
            ref={stickyHeaderExtrasRef}
            style={{
              maxHeight: '30vh',
              overflowY: 'auto',
              paddingTop: '8px',
              paddingBottom: '8px',
            }}
          >
            {renderHeaderExtras({ bookings: nearestBookings, centerTime })}
          </div>
        )}

        <Box
          ref={timelineContainerRef}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            backgroundColor: 'background.paper',
            boxShadow: theme => `0 1px 0 ${theme.palette.divider}`,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <Box sx={{ width: 'max-content', position: 'relative', paddingLeft: `${GRID_START_OFFSET_PX}px` }}>
            {redLineVisible && (
              <RedLine
                sx={{
                  left: redLinePosition - GRID_START_OFFSET_PX,
                  height: '100%',
                  zIndex: 5,
                  boxShadow: '0 0 4px rgba(255, 0, 0, 0.8)',
                }}
              />
            )}
            {renderTimeline &&
              renderTimeline({
                times,
                slotWidthPx,
                showTimeRow: true,
                showSlotRows: false,
                hideTimeRow: false,
              })}
          </Box>
        </Box>

      </Box>

      <Paper
        ref={containerRef}
        sx={{
          width: '100%',
          overflowX: 'auto',
          position: 'relative',
          overflowY: 'visible',
          marginTop: 0,
          minHeight: heightCalculator(rowCount),
          height: 'auto',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          '&:hover': {
            cursor: 'grab',
          },
          '&:active': {
            cursor: 'grabbing',
          },
          '&::-webkit-scrollbar': {
            display: 'none',
            width: 0,
            height: 0,
          },
          '&::-webkit-scrollbar-track': {
            display: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '-ms-overflow-style': 'none',
        }}
      >
        {isLoadingShiftTimes && (
          <LoadingOverlay
            style={{
              left: `${GRID_START_OFFSET_PX}px`,
              width: `calc(${slotWidthPx}px * ${times.length})`,
            }}
          />
        )}
        {availableShiftTimes &&
          !isLoadingShiftTimes &&
          (() => {
            if (!times || times.length === 0 || !draggedBookingDuration) return null;

            const pxPerMinute = (slotWidthPx * (60 / minutesPerSlot)) / 60;

            const normalizeKey = t => {
              const transformed = shiftTimeOffsetMinutes
                ? applyOffsetToTime(String(t), shiftTimeOffsetMinutes)
                : String(t);
              const [hh, mm] = String(transformed).split(':');
              return `${String(parseInt(hh, 10)).padStart(2, '0')}:${String(
                parseInt(mm, 10)
              ).padStart(2, '0')}`;
            };

            // Build sets: available (normally) and overbookableOnly (overbookedBy > 0)
            const { availableSet, overbookableSet } = (() => {
              const av = new Set();
              const ob = new Set();
              try {
                if (!Array.isArray(availableShiftTimes))
                  return { availableSet: av, overbookableSet: ob };
                for (const entry of availableShiftTimes) {
                  if (!entry) continue;
                  if (typeof entry === 'string') {
                    av.add(normalizeKey(entry));
                    continue;
                  }
                  if (!entry.startTime) continue;
                  const key = normalizeKey(entry.startTime);
                  if (Number(entry.overbookedBy) > 0) {
                    ob.add(key);
                  } else {
                    av.add(key);
                  }
                }
              } catch {
                /* noop */
              }
              return { availableSet: av, overbookableSet: ob };
            })();

            // Compute available minutes (absolute minutes since midnight) from availableSet
            const toMinutes = str => {
              const [hh, mm] = String(str).split(':').map(Number);
              return Number.isFinite(hh) && Number.isFinite(mm) ? hh * 60 + mm : null;
            };
            const availableMinutes = Array.from(availableSet)
              .map(toMinutes)
              .filter(v => v !== null)
              .sort((a, b) => a - b);
            const overbookableMinutes = Array.from(overbookableSet)
              .map(toMinutes)
              .filter(v => v !== null)
              .sort((a, b) => a - b);

            // Generate intervals strictly per valid start time [start, start+duration]
            const durationPx = draggedBookingDuration * pxPerMinute;
            const gridStartPx = GRID_START_OFFSET_PX;
            const gridEndPx = GRID_START_OFFSET_PX + slotWidthPx * times.length;
            const buildIntervals = mins =>
              mins.map(absMin => {
                const startPx = GRID_START_OFFSET_PX + pxPerMinute * (absMin - 9 * 60);
                const endPx = startPx + durationPx;
                return { start: Math.max(gridStartPx, startPx), end: Math.min(gridEndPx, endPx) };
              });

            const mergeIntervals = intervals => {
              const merged = [];
              if (intervals.length > 0) {
                intervals.sort((a, b) => a.start - b.start);
                merged.push({ ...intervals[0] });
                for (let i = 1; i < intervals.length; i++) {
                  const lastMerged = merged[merged.length - 1];
                  const current = intervals[i];
                  if (current.start <= lastMerged.end) {
                    lastMerged.end = Math.max(lastMerged.end, current.end);
                  } else {
                    merged.push({ ...current });
                  }
                }
              }
              return merged;
            };

            const mergedGreenIntervals = mergeIntervals(buildIntervals(availableMinutes));
            const rawYellowIntervals = buildIntervals(overbookableMinutes);
            const mergedYellowIntervals = mergeIntervals(rawYellowIntervals);

            // Subtract green from yellow so yellow only shows overbook-only regions
            const subtractIntervals = (base, subtract) => {
              const result = [];
              for (const b of base) {
                const pieces = [{ ...b }];
                for (const s of subtract) {
                  for (let i = pieces.length - 1; i >= 0; i--) {
                    const p = pieces[i];
                    if (s.end <= p.start || s.start >= p.end) continue;
                    pieces.splice(i, 1);
                    if (s.start > p.start) pieces.push({ start: p.start, end: Math.min(p.end, s.start) });
                    if (s.end < p.end) pieces.push({ start: Math.max(p.start, s.end), end: p.end });
                  }
                }
                result.push(...pieces);
              }
              return result.filter(p => p.end > p.start);
            };

            const yellowOnlyIntervals = subtractIntervals(mergedYellowIntervals, mergedGreenIntervals);

            // Red = complement of (green ∪ yellow) across the full grid width
            const coveredIntervals = mergeIntervals([
              ...mergedGreenIntervals,
              ...yellowOnlyIntervals,
            ]);
            const redIntervals = [];
            if (gridEndPx > gridStartPx) {
              let cursor = gridStartPx;
              for (const gi of coveredIntervals) {
                const s = Math.max(gridStartPx, gi.start);
                const e = Math.min(gridEndPx, gi.end);
                if (cursor < s) {
                  redIntervals.push({ start: cursor, end: s });
                }
                cursor = Math.max(cursor, e);
              }
              if (cursor < gridEndPx) {
                redIntervals.push({ start: cursor, end: gridEndPx });
              }
            }

            const redOverlays = redIntervals.map((interval, idx) => (
              <div
                key={`overlay-red-cmp-${idx}`}
                style={{
                  position: 'absolute',
                  left: `${interval.start}px`,
                  top: 0,
                  width: `${interval.end - interval.start}px`,
                  height: '100%',
                  backgroundColor: '#FF6347',
                  opacity: 0.4,
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              />
            ));

            const yellowOverlays = yellowOnlyIntervals.map((interval, index) => (
              <div
                key={`overlay-yellow-${index}`}
                style={{
                  position: 'absolute',
                  left: `${interval.start}px`,
                  top: 0,
                  width: `${interval.end - interval.start}px`,
                  height: '100%',
                  backgroundColor: '#FFD700',
                  opacity: 0.4,
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              />
            ));

            const greenOverlays = mergedGreenIntervals.map((interval, index) => (
              <div
                key={`overlay-green-${index}`}
                style={{
                  position: 'absolute',
                  left: `${interval.start}px`,
                  top: 0,
                  width: `${interval.end - interval.start}px`,
                  height: '100%',
                  backgroundColor: '#7CFC00',
                  opacity: 0.4,
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              />
            ));

            const startMarkers = [...availableMinutes, ...overbookableMinutes].map(
              (absMin, idx) => (
                <div
                  key={`start-marker-${idx}-${absMin}`}
                  style={{
                    position: 'absolute',
                    left: `${GRID_START_OFFSET_PX + pxPerMinute * (absMin - 9 * 60)}px`,
                    top: '0',
                    width: '2px',
                    height: '100%',
                    backgroundColor:
                      theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.45)' : 'rgba(255, 255, 255, 0.85)',
                    boxShadow: '0 0 5px rgba(0, 0, 0, 0.3)',
                    zIndex: 1,
                    pointerEvents: 'none',
                  }}
                />
              )
            );

            return (
              <>
                {redOverlays}
                {yellowOverlays}
                {greenOverlays}
                {startMarkers}
              </>
            );
          })()}
        {redLineVisible && (
          <RedLine
            sx={{
              left: redLinePosition,
              height: heightCalculator(rowCount),
              zIndex: 5,
              boxShadow: '0 0 4px rgba(255, 0, 0, 0.8)',
            }}
          />
        )}

        {/* Lane backgrounds (resource lanes) rendered with low opacity behind content */}
        {Array.isArray(laneBackgrounds) &&
          laneBackgrounds.length > 0 &&
          laneBackgrounds.map(lb => (
            <div
              key={`lane-bg-${lb.index}`}
              style={{
                position: 'absolute',
                left: '27px',
                top: rowTopOffset + (lb.index - 1) * rowHeight,
                width: times.length > 0 ? `calc(${slotWidthPx}px * ${times.length})` : '100%',
                height: `${rowHeight}px`,
                backgroundColor: lb.color,
                opacity: typeof lb.opacity === 'number' ? lb.opacity : 0.18,
                zIndex: 0,
                pointerEvents: 'none',
              }}
            />
          ))}

        {/* Lane labels: sticky overlay on the left, stays visible while horizontal scrolling */}
        {typeof renderLaneLabel === 'function' && (
          <div
            style={{
              position: 'sticky',
              left: 0,
              top: 0,
              height: 0,
              zIndex: 2,
              pointerEvents: 'none',
              overflow: 'visible',
            }}
          >
            {Array.from({ length: Math.max(1, rowCount) }).map((_, laneIdx) => (
              <div
                key={`lane-label-${laneIdx + 1}`}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: rowTopOffset + laneIdx * rowHeight,
                  minWidth: `${laneLabelWidth}px`,
                  height: `${rowHeight}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  userSelect: 'none',
                  paddingLeft: '2px',
                }}
              >
                <div>{renderLaneLabel({ index: laneIdx + 1 })}</div>
              </div>
            ))}
          </div>
        )}

        {times.map(time => (
          <GridLine
            key={time.i}
            sx={{
              zIndex: 1,
              left: `calc(${GRID_START_OFFSET_PX}px + ${slotWidthPx} * ${time.i}px)`,
              height: '200%',
            }}
          />
        ))}
        {renderTimeline &&
          renderTimeline({
            times,
            slotWidthPx,
            showTimeRow: true,
            showSlotRows: true,
            hideTimeRow: true,
          })}
        {bookings.map((booking, index) => renderBookingCard(booking, index, rowHeight))}
      </Paper>
    </>
  );
}
